import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../components/UI/Button/Button';
import styles from './PurchaseFlatPageStageOne.module.css';
import { calculateDiscountedPrice } from '../../../../utils/discountPriceUtils';
import cn from 'classnames';

type BuyPageStageOneProps = {
    selectedOption: string;
    flat: {
        roomType: string;
        area: string;
        floor: number;
        entrance: number;
        id: number;
        price: string;
        pricePerM2?: string;
        discountProperty?: number;
        discountFull?: number;
    };
    handleNextStage: () => void;
};

const BuyPageStageOne: React.FC<BuyPageStageOneProps> = ({
    selectedOption,
    flat,
    handleNextStage,
}) => {
    const navigate = useNavigate();

    const parsePrice = (price: string | number | null | undefined): number => {
        if (price === null || price === undefined) {
            return 0;
        }
        if (typeof price === 'number') {
            return price;
        }
        if (typeof price === 'string') {
            return Number(price.replace(/\s/g, '')) || 0;
        }
        return 0;
    };

    const basePrice = parsePrice(flat.price ?? '0');
    const discountedPrice = calculateDiscountedPrice(
        flat.pricePerM2 ?? '0',
        flat.discountProperty ?? 0,
        flat.price ?? '0'
    );
    const computedPrice = parsePrice(discountedPrice);

    const fullPaymentAmount = computedPrice - (flat.discountFull || 0);
    
    const installmentPrice = basePrice;

    const installmentYears = 4;
    const downPayment = Math.round(installmentPrice * 0.3);
    const monthlyPayment = Math.round((installmentPrice - downPayment) / (installmentYears * 12));

    const displayedPrice = selectedOption === 'full' ? fullPaymentAmount : installmentPrice;

    return (
        <>
            <div className={styles.text_wrapper}>
                <h3>Бронирование</h3>
                <p className='text_body_b2'>Для приобретения квартиры сперва нужно её&nbsp;забронировать, чтобы зафиксировать стоимость. Бронь действует 7&nbsp;суток&nbsp;&mdash; в&nbsp;течение этого времени необходимо посетить наш офис.</p>
            </div>
            <div className={styles.content_wrapper}>
                <div className={styles.content_card_wrapper}>
                    <div className={styles.content_card_title}>
                        <p className={'text_subheaders_sh1'}>Условия покупки</p>
                    </div>
                    <div className={styles.content_info}>
                        <div className={styles.info_row}>
                            <span className={cn('text_body_b2', styles.info_label)}>Стоимость</span>
                            <span className={cn('text_subheaders_sh2', styles.info_value)}>
                                от {displayedPrice.toLocaleString('ru-RU')} ₽
                            </span>
                        </div>
                        <div className={styles.info_row}>
                            <span className={cn('text_body_b2', styles.info_label)}>Программа</span>
                            <span className={cn('text_subheaders_sh2', styles.info_value)}>
                                {selectedOption === 'full' ? 'Полная оплата' : 'Рассрочка'}
                            </span>
                        </div>
                        {selectedOption === 'installment' && (
                            <>
                                <div className={styles.info_row}>
                                    <span className={cn('text_body_b2', styles.info_label)}>Первый взнос</span>
                                    <span className={cn('text_subheaders_sh2', styles.info_value)}>
                                        от {downPayment.toLocaleString('ru-RU') || '0'} ₽
                                    </span>
                                </div>
                                <div className={styles.info_row}>
                                    <span className={cn('text_body_b2', styles.info_label)}>Ежемесячный взнос</span>
                                    <span className={cn('text_subheaders_sh2', styles.info_value)}>
                                        от {monthlyPayment.toLocaleString('ru-RU')} ₽
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                    <div className={styles.content_return_wrapper}>
                        <p className='text_body_b3'>
                            Чтобы изменить условия, достаточно <span className={styles.link} onClick={() => navigate(`/flat/status/${flat.id}`)}>вернуться на&nbsp;страницу квартиры</span>
                        </p>
                    </div>
                </div>
                <div className={styles.content_card_wrapper}>
                    <div className={styles.content_card_title}>
                        <p className='text_subheaders_sh1'>
                            {flat.roomType !== "Студия" ? `${flat.roomType} квартира` : flat.roomType}
                        </p>
                    </div>
                    <div className={styles.content_info}>
                        <div className={styles.info_row}>
                            <span className={cn('text_body_b2', styles.info_label)}>Общая площадь</span>
                            <span className={cn('text_subheaders_sh2', styles.info_value)}>
                                от {flat.area} м²
                            </span>
                        </div>
                        <div className={styles.info_row}>
                            <span className={cn('text_body_b2', styles.info_label)}>Этаж</span>
                            <span className={cn('text_subheaders_sh2', styles.info_value)}>
                                {flat.floor}
                            </span>
                        </div>
                        <div className={styles.info_row}>
                            <span className={cn('text_body_b2', styles.info_label)}>Подъезд</span>
                            <span className={cn('text_subheaders_sh2', styles.info_value)}>
                                {flat.entrance}
                            </span>
                        </div>
                        <div className={styles.info_row}>
                            <span className={cn('text_body_b2', styles.info_label)}>Проект</span>
                            <span className={cn('text_subheaders_sh2', styles.info_value)}>
                                Статус
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.fixed_button_wrapper}>
                <div className={styles.fixed_button}>
                    <Button
                        colorType='blue'
                        roundType='small_rounded'
                        type='submit'
                        width='100%'
                        onClick={handleNextStage}
                    >
                        Продолжить
                    </Button>
                    <p className={cn('text_captions_c1', styles.underbutton_span)}>
                        <span>Стоимость носит информационный характер и&nbsp;не&nbsp;является публичной офертой.</span>
                    </p>
                </div>
            </div>
        </>
    );
};

export default BuyPageStageOne;
